/**
 * js-expandable — Collapsible content with a max-height toggle.
 *
 * Markup requirements:
 *   - Add `.js-expandable` to the content element you want to collapse.
 *   - Optionally set `data-collapsed-height="300px"` on it to reveal a preview
 *     sliver while collapsed. Default is `0` (fully hidden until expanded).
 *   - Add `data-expand-target="<CSS selector>"` to the trigger button, pointing
 *     at the `.js-expandable` element.
 *   - To scope the lookup to a nearest ancestor (e.g. inside a loop card), also
 *     add `data-expand-scope="<ancestor CSS selector>"` to the trigger button.
 *   - Add `data-expand-mobile-only` to the trigger button to restrict the
 *     expand/collapse behaviour to viewports narrower than 1024px. On wider
 *     viewports the target's max-height constraint is removed entirely so the
 *     content is always fully visible.
 *   - Optional: set `data-expand-label-collapsed` and `data-expand-label-expanded`
 *     on the trigger to swap the visible label on toggle. If the trigger contains
 *     a `<span data-expand-text>`, only that span's text is replaced — preserving
 *     nested icons. Otherwise the first non-whitespace text node is replaced.
 *   - Optional: add `data-expand-exclusive` to a wrapping ancestor to enable
 *     accordion-style behavior. When any trigger inside that ancestor opens,
 *     every other open expandable within the same ancestor collapses first.
 *     Per the W3C APG accordion pattern, each trigger inside an exclusive group
 *     should be wrapped in a heading (`<h2>`/`<h3>`/`<h4>`) so AT users can
 *     navigate the panels by heading.
 *
 * Classes applied automatically:
 *   - `.is-expanded`    — added to the target while expanded.
 *   - `.has-expandable` — added to the nearest grid/looper ancestor on init.
 *   - `.has-expanded`   — added to the nearest grid/looper ancestor when any
 *                         child is expanded; removed when all are collapsed.
 *
 * Accessibility:
 *   - `aria-expanded` on the trigger is kept in sync automatically.
 *   - Triggers rendered with non-interactive tags (anything other than
 *     `<button>`/`<a>`) get an Enter/Space keydown handler so keyboard
 *     activation matches the click path. The editor stamps `role="button"` +
 *     `tabindex="0"` for those tags at save time.
 *   - When a target has no preview sliver (`data-collapsed-height` is unset
 *     or `0`), it is marked `inert` while collapsed so it leaves the focus
 *     order and the accessibility tree.
 * Editor: click handlers are skipped inside the Gutenberg block editor.
 */

import {
	MOBILE_BREAKPOINT,
	EXPANDABLE_PARENT_SELECTORS,
	DATA_EXPAND_LABEL_COLLAPSED,
	DATA_EXPAND_LABEL_EXPANDED,
	DATA_EXPAND_ICON_EXPANDED,
	DATA_EXPAND_TEXT,
	DATA_EXPAND_EXCLUSIVE,
	EXCLUSIVE_AUTO_SELECTORS,
} from '../../shared/expandable-constants.js';

// Inlined by PHP (see generateprop_attach_expand_icon_registry): a flat
// { iconId: svgString } map used to swap `.gb-shape` contents on state change.
function getIconRegistry() {
	return (
		(typeof window !== 'undefined' &&
			window.generatepropExpandIconRegistry) ||
		{}
	);
}

function updateParentStates(target, scopeElement) {
	EXPANDABLE_PARENT_SELECTORS.forEach((selector) => {
		const parent = target.closest(selector);
		if (!parent) return;
		const hasExpanded = !!parent.querySelector(
			'.js-expandable.is-expanded'
		);
		parent.classList.toggle('has-expanded', hasExpanded);
	});

	if (scopeElement) {
		const hasExpanded = !!scopeElement.querySelector(
			'.js-expandable.is-expanded'
		);
		scopeElement.classList.toggle('has-expanded', hasExpanded);
	}
}

const isEditor = typeof wp !== 'undefined' && !!wp.data;

// Tracks ResizeObserver instances keyed by target element.
const resizeObservers = new Map();

function observeChildResize(target) {
	if (resizeObservers.has(target)) return;

	const ro = new ResizeObserver(() => {
		target.style.setProperty(
			'--expand-height',
			target.scrollHeight + 'px'
		);
	});

	// Observe direct children so height changes bubble up correctly.
	Array.from(target.children).forEach((child) => ro.observe(child));
	resizeObservers.set(target, ro);
}

/**
 * Locate the text-bearing node inside a trigger. Returns an object with a
 * unified setter so callers don't need to care which container form it found.
 *
 * Priority:
 *   1. `<span data-expand-text>` — explicit opt-in, preserves nested icons.
 *   2. `<span class="gb-text">`  — GenerateBlocks native text wrapper (buttons).
 *   3. First non-whitespace text node on the button.
 * Returns null if nothing usable is found.
 */
function findTriggerTextTarget(button) {
	const explicit = button.querySelector(`[${DATA_EXPAND_TEXT}]`);
	if (explicit) {
		return {
			get: () => explicit.textContent,
			set: (value) => {
				explicit.textContent = value;
			},
		};
	}

	const gbText = button.querySelector('.gb-text');
	if (gbText) {
		return {
			get: () => gbText.textContent,
			set: (value) => {
				gbText.textContent = value;
			},
		};
	}

	for (const node of button.childNodes) {
		if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim() !== '') {
			return {
				get: () => node.nodeValue,
				set: (value) => {
					node.nodeValue = value;
				},
			};
		}
	}

	return null;
}

/**
 * Swap the icon inside the trigger's `.gb-shape` based on expanded state.
 * Caches the original SVG on first call so collapsing restores it exactly.
 * No-op when the trigger has no `data-expand-icon-expanded` or no `.gb-shape`.
 */
function swapTriggerIcon(button, isExpanded) {
	const iconId = button.getAttribute(DATA_EXPAND_ICON_EXPANDED);
	if (!iconId) return;

	const shape = button.querySelector('.gb-shape');
	if (!shape) return;

	if (button.__expandOriginalIconHtml === undefined) {
		button.__expandOriginalIconHtml = shape.innerHTML;
	}

	const registry = getIconRegistry();
	const expandedSvg = registry[iconId];
	const next = isExpanded
		? expandedSvg || button.__expandOriginalIconHtml
		: button.__expandOriginalIconHtml;

	if (shape.innerHTML !== next) shape.innerHTML = next;
}

/**
 * Swap the trigger's visible label based on expanded state.
 * Either data-expand-label-collapsed / -expanded may be absent — when absent,
 * the button's original editor text (captured on first call) is used instead.
 */
function swapTriggerLabel(button, isExpanded) {
	const target = findTriggerTextTarget(button);
	if (!target) return;

	if (button.__expandOriginalLabel === undefined) {
		button.__expandOriginalLabel = target.get();
	}
	const original = button.__expandOriginalLabel;

	const collapsedLabel = button.getAttribute(DATA_EXPAND_LABEL_COLLAPSED);
	const expandedLabel = button.getAttribute(DATA_EXPAND_LABEL_EXPANDED);

	const next = isExpanded
		? expandedLabel || original
		: collapsedLabel || original;

	if (target.get() !== next) target.set(next);
}

/**
 * Apply an expanded/collapsed state to a target and sync its trigger button's
 * UI (aria-expanded, label, icon) and parent-container state classes. Used by
 * both the click handler and the accordion-style collapse-others path so the
 * two stay perfectly in sync.
 */
// True when the target is fully hidden while collapsed — i.e. there's no
// preview sliver. Only these targets are safe to mark `inert`, since marking
// a partially-visible preview inert would strand visible-but-uninteractable
// content.
function hasNoPreviewSliver(target) {
	const value = target.getAttribute('data-collapsed-height');
	return !value || value === '0';
}

/**
 * Keep the trigger button's viewport position fixed while a target collapses,
 * so the content appears to retract *toward* the button instead of the page
 * shrinking out from under the user. Only needed on collapse (the page is
 * getting shorter); expand grows around the button and needs no correction.
 *
 * Self-correcting and direction-agnostic: when the target sits below the
 * trigger the delta stays ~0 and no scroll happens; when it sits above, the
 * per-frame correction tracks the shrink exactly.
 */
function pinTriggerDuringCollapse(button, target) {
	const prefersReduced =
		window.matchMedia &&
		window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	const prevTop = button.getBoundingClientRect().top;
	const correct = () => {
		const delta = button.getBoundingClientRect().top - prevTop;
		if (delta) window.scrollBy(0, delta);
	};

	// Reduced motion (or no transition): height changes synchronously, so a
	// single post-layout correction is enough.
	if (prefersReduced) {
		requestAnimationFrame(correct);
		return;
	}

	let rafId = requestAnimationFrame(function loop() {
		correct();
		rafId = requestAnimationFrame(loop);
	});

	const stop = (event) => {
		if (event && event.propertyName !== 'max-height') return;
		cancelAnimationFrame(rafId);
		correct(); // final settle
		target.removeEventListener('transitionend', stop);
		clearTimeout(safety);
	};
	target.addEventListener('transitionend', stop);
	// Safety net in case transitionend never fires (e.g. interrupted toggle).
	const safety = setTimeout(stop, 500);
}

function applyExpandedState(button, target, expanded, scopeElement) {
	if (expanded) {
		target.style.setProperty(
			'--expand-height',
			target.scrollHeight + 'px'
		);
	}
	target.classList.toggle('is-expanded', expanded);

	if (hasNoPreviewSliver(target)) {
		if (expanded) target.removeAttribute('inert');
		else target.setAttribute('inert', '');
	}

	button.setAttribute('aria-expanded', String(expanded));
	swapTriggerLabel(button, expanded);
	swapTriggerIcon(button, expanded);
	updateParentStates(target, scopeElement);
}

function initExpandable() {
	// Auto-stamp `data-expand-exclusive` on any container matching a registered
	// auto-selector. Lets design-system patterns (e.g. the Success Stories grid)
	// opt entire groupings into accordion behavior without editing page markup.
	EXCLUSIVE_AUTO_SELECTORS.forEach((selector) => {
		document.querySelectorAll(selector).forEach((el) => {
			if (!el.hasAttribute(DATA_EXPAND_EXCLUSIVE)) {
				el.setAttribute(DATA_EXPAND_EXCLUSIVE, '');
			}
		});
	});

	document.querySelectorAll('.js-expandable').forEach((target) => {
		if (target.dataset.expandableInit) return;
		target.dataset.expandableInit = '1';

		const collapsedHeight =
			target.getAttribute('data-collapsed-height') || '0';
		target.style.setProperty('--collapsed-height', collapsedHeight);

		// Initial AT/focus state for fully collapsed targets. Targets with a
		// preview sliver stay reachable so visible content remains operable.
		if (
			hasNoPreviewSliver(target) &&
			!target.classList.contains('is-expanded')
		) {
			target.setAttribute('inert', '');
		}

		EXPANDABLE_PARENT_SELECTORS.forEach((selector) => {
			const parent = target.closest(selector);
			if (parent) parent.classList.add('has-expandable');
		});

		observeChildResize(target);
	});

	if (isEditor) return;

	document.querySelectorAll('[data-expand-target]').forEach((button) => {
		if (button.dataset.expandListenerInit) return;
		button.dataset.expandListenerInit = '1';
		const mobileOnly = button.hasAttribute('data-expand-mobile-only');
		const targetSelector = button.getAttribute('data-expand-target');
		const scopeSelector = button.getAttribute('data-expand-scope');
		const scopeElement = scopeSelector
			? button.closest(scopeSelector)
			: null;
		if (scopeElement) scopeElement.classList.add('has-expandable');

		// Resolve and link the target eagerly so collapse-others (and any
		// other external mutators) can find the trigger that owns a target.
		const resolveTarget = () =>
			scopeElement
				? scopeElement.querySelector(targetSelector)
				: document.querySelector(targetSelector);

		const initialTarget = resolveTarget();
		if (initialTarget) {
			initialTarget.__expandTrigger = button;
			initialTarget.__expandScopeElement = scopeElement;

			// Wire aria-controls so AT announces which region this trigger
			// operates. Reuse the target's existing data-expand-id as its id.
			// Legacy class-selector triggers may have no id — skip silently.
			const expandId = initialTarget.getAttribute('data-expand-id');
			if (expandId) {
				if (!initialTarget.id) initialTarget.id = expandId;
				button.setAttribute('aria-controls', initialTarget.id);
			}
		}

		// Triggers stamped with `role="button"` on a non-button/anchor tag
		// don't get native Enter/Space activation. Bridge it to click() so
		// the toggle path stays a single source of truth.
		const tag = button.tagName;
		if (tag !== 'BUTTON' && tag !== 'A') {
			button.addEventListener('keydown', (event) => {
				if (event.target !== button) return;
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					button.click();
				}
			});
		}

		button.addEventListener('click', () => {
			// Do nothing on desktop when mobile-only mode is active.
			if (mobileOnly && window.innerWidth >= MOBILE_BREAKPOINT) return;

			const target = resolveTarget();
			if (!target) return;

			// Refresh links in case the target was re-resolved (e.g. after a
			// FacetWP reload swapped DOM nodes).
			target.__expandTrigger = button;
			target.__expandScopeElement = scopeElement;

			const isExpanded = target.classList.contains('is-expanded');

			// Accordion-style: when opening inside a [data-expand-exclusive]
			// ancestor, collapse every other open expandable in that group
			// first so only one stays open at a time.
			if (!isExpanded) {
				const exclusiveAncestor = button.closest(
					`[${DATA_EXPAND_EXCLUSIVE}]`
				);
				if (exclusiveAncestor) {
					exclusiveAncestor
						.querySelectorAll('.js-expandable.is-expanded')
						.forEach((other) => {
							if (other === target) return;
							const otherButton = other.__expandTrigger;
							if (!otherButton) return;
							applyExpandedState(
								otherButton,
								other,
								false,
								other.__expandScopeElement || null
							);
						});
				}
			}

			const willExpand = !isExpanded;
			applyExpandedState(button, target, willExpand, scopeElement);
			if (!willExpand) pinTriggerDuringCollapse(button, target);
		});

		// Initialize label + icon caches in their collapsed state.
		swapTriggerLabel(button, false);
		swapTriggerIcon(button, false);

		// When mobile-only, stamp a modifier class on the target and keep state in sync.
		if (mobileOnly) {
			// Mark the target so SCSS can apply desktop overrides.
			if (initialTarget)
				initialTarget.classList.add('js-expandable--mobile-only');

			const syncMobileState = () => {
				const target = resolveTarget();
				if (!target) return;

				if (window.innerWidth >= MOBILE_BREAKPOINT) {
					// Desktop: reset expanded state. The CSS `--mobile-only`
					// rule already removes the max-height constraint, so the
					// content is fully visible — clear `inert` to match.
					target.classList.remove('is-expanded');
					target.removeAttribute('inert');
					button.setAttribute('aria-expanded', 'false');
					swapTriggerLabel(button, false);
					swapTriggerIcon(button, false);
					updateParentStates(target, scopeElement);
				} else if (hasNoPreviewSliver(target)) {
					// Back to mobile: re-inert any collapsed target whose
					// inert state we cleared above.
					if (!target.classList.contains('is-expanded')) {
						target.setAttribute('inert', '');
					}
				}
			};

			window.addEventListener('resize', syncMobileState);
			// Run once on init to handle the case where the page loads on desktop.
			syncMobileState();
		}
	});
}

document.addEventListener('DOMContentLoaded', initExpandable);
document.addEventListener('facetwp-loaded', initExpandable);
